import {
  type CoreEditor,
  type EditorState,
  getVersion,
  type JSONContent,
  sendableSteps,
  type SendableWordCountChange,
  sendableWordCountChange,
  Step,
} from "@vericus/cadmus-editor-prosemirror";

import { Channel, Presence } from "phoenix";
import { debounce } from "ts-debounce";

import { API_ENDPOINT } from "@/config";
import { StudentChannel } from "@/stores/student-channel";

import { PresenceMeta } from "./local-collab-presence";

// Constants
const SAVE_DEBOUNCE = 2000; /// Debounce time in milliseconds for sending steps
const POLL_INTERVAL = 8000; // Polling interval in milliseconds

/**
 * Initialisation props for a StreamConnection on an Editor.
 */
export interface StreamConnectionProps {
  /** The editor that is wrapped and connected for collaboration. */
  editor: CoreEditor;
  /** Cadmus Tenant */
  tenant: string;
  /** Collab Authority Stream UUID specific to the Editor instance. */
  streamId: string;
  /** Client ID */
  clientId: string;
  /** Base state of the Stream or the Editor that is considered version 0. */
  initialState: JSONContent;
  /** The StudentChannel for extra assistance in collaboration */
  studentChannel?: StudentChannel;
  /** Callback to write a confirmed version snapshot. */
  onSnapshot?: (snapshot: SnapshotInput) => void;
  /** Callback to trigger complete failure and need to reset the document. */
  onFailure?: () => void;
}

/**
 * Connect a TipTap `Editor` to a collaborative Authority's `Stream`.
 *
 * ## Authority Stream
 *
 * A `Stream` is an append-only data structure storing ordered
 * operation-transform `Step` steps. Steps can only be appended to the stream if
 * the client provided version matches, otherwise the client must first rebase
 * it's local state and catch up before trying a subsequent write.
 *
 * The `Editor` instance has a confirmed internal `version` number and a buffer
 * of un-confirmed steps (provided by the `collab` extension and plugin).
 *
 * Connecting the `Editor` to a back-end Authority Stream will:
 *
 *  1. Load the initial state of the Editor by catching up to the latest steps.
 *
 *  2. Regularly listen to the Work Channel for new steps.
 *
 *  3. Send local steps to the Authority server to append to the stream and
 *     handle the re-basing on conflicts.
 *
 *  4. Generate snapshots for confirmed versions of the EditorState.
 *
 * ## Communication channels
 *
 * The `Editor` can be *connected* to Authority Stream via the REST API and a
 * WebSocket channel. The REST API is used for transactional and stateless
 * operations for writing and reading a Stream. The WebSocket channel is used
 * for eagerly listening to writes from other clients and syncing carets for
 * visual fidelity.
 *
 * NOTE: The absence of the websocket connection should not have a critical
 * impact as the transactional nature for the REST API will assist the Editor in
 * catching up on conflicts.
 *
 * The Cadmus Authority REST API exposes the following endpoints:
 *
 *   1. `AppendSteps` - Append steps on a Stream at the expected version.
 *   Returns 204 accepted or 409 conflict.
 *
 *   2. `ReadSteps` - Read steps from a version.
 *
 * The WebSocket channel uses an existing `Phoenix.Channel` connected to a
 * Pantheon Work topic.
 *
 * Channel listens to the messages:
 *
 *   1. `stream.version` with payload `StreamVersionMsg`.
 *
 * Channel pushes the messages:
 *
 *   1. `client.selection` with payload `EditorSelectionMsg`.
 *
 * ## Usage
 *
 * Initialise the connection instance:
 *
 *     const editor = createCadmusEditor({ ...props })
 *     const conn = new StreamConnection({ editor, ...props });
 *
 * Asynchronously await the connection:
 *
 *     await conn.connect();
 *
 * The `connect()` is asynchronous and **must** be awaited to ensure the
 * `Editor` is ready for interaction.
 */
export class StreamConnection {
  editor: CoreEditor;
  tenant: string;
  streamId: string;
  clientId: string;
  httpClient: AuthorityAPI;

  private studentTopic?: string;
  private channel?: Channel;
  private presence?: Presence;

  private onSnapshot?: (snapshot: {
    doc: JSONContent;
    version: number;
    wordCountChange: SendableWordCountChange;
  }) => void;
  private onFailure?: () => void;

  private initialState: JSONContent;
  private comm: CommState = CommState.INIT;

  private channelRefs: Array<[string, number]> = [];
  private pollTimerRef: NodeJS.Timeout | undefined = undefined;

  constructor(props: StreamConnectionProps) {
    this.tenant = props.tenant;
    this.editor = props.editor;
    this.streamId = props.streamId;
    this.clientId = props.clientId;
    this.onSnapshot = props.onSnapshot;
    this.onFailure = props.onFailure;
    this.initialState = props.initialState;

    // Select a channel based on the context.
    this.studentTopic = props.studentChannel?.topic;
    this.channel = props.studentChannel?.channel;
    this.presence = props.studentChannel?.presence;

    this.httpClient = new AuthorityAPI({
      tenant: props.tenant,
      streamId: props.streamId,
      studentTopic: this.studentTopic,
    });
  }

  /**
   * Setup the connection and start collaboration.
   *
   * Ensures that the Stream is ready and the Editor is caught up to the latest
   * confirmed version before interaction can begin.
   */
  connect = async () => {
    // Ensure the Stream is created in the backend
    await this.httpClient.setupAuthorityStream(this.initialState);

    // Save local changes
    const debouncedSendSteps = debounce(this.sendSteps, SAVE_DEBOUNCE, {
      maxWait: SAVE_DEBOUNCE * 4,
    });
    this.editor.on("update", debouncedSendSteps);

    // Carets sync on focus
    this.editor.on("selectionUpdate", ({ transaction }) => {
      if (!transaction.docChanged) {
        this.sendCaret();
      }
    });

    // And cleanup when done
    this.editor.on("destroy", () => {
      this.clean();
    });

    // Begin by catching up to the latest state.
    await this.poll();

    const sendable = sendableSteps(this.editor.state);
    // eslint-disable-next-line no-console
    console.assert(
      sendable === null,
      "Sendable steps should be empty after load",
      sendable
    );

    // Listen to changes
    this.listen();
  };

  // Accept steps from a `version` onwards into this Editor.
  private receiveSteps = (
    steps: readonly Step[],
    clientIds: string[],
    version: number,
    onNewState?: (state: EditorState) => void
  ): boolean => {
    if (steps.length === 0) return true;
    if (getVersion(this.editor.state) !== version) {
      console.warn(
        "Received steps but editor has moved on from version",
        version,
        this.comm
      );
      return false;
    }
    try {
      this.editor.commands.receiveSteps(steps, clientIds, onNewState);
    } catch (e) {
      console.error(e);
      this.comm = CommState.FAIL;
      this.onFailure?.();
      this.clean();
    }

    return true;
  };

  // Single poll to the Authority Stream to read the latest steps since this
  // Editor's current version.
  private poll = async () => {
    // FAIL state nothing to do
    if (this.comm === CommState.FAIL) {
      return;
    }

    const { steps, version } = await this.httpClient.readSteps(
      getVersion(this.editor.state)
    );

    if (this.comm === CommState.SEND) return;

    this.receiveSteps(
      steps.map((s) => Step.fromJSON(this.editor.schema, s.data)),
      steps.map((s) => s.client_id),
      version
    );

    this.drawCarets();
  };

  // Listen for new changes on the Stream either listening on the WorkChannel if
  // available, or regular polling.
  private listen = async () => {
    if (this.channel) {
      const versionRef = this.channel.on(
        "stream.version",
        (payload: StreamVersionMsg) => {
          if (
            payload.stream_id === this.streamId &&
            payload.version > getVersion(this.editor.state) &&
            payload.client_id !== this.clientId
          ) {
            this.poll();
          }
        }
      );
      this.channelRefs = [["stream.version", versionRef]];
    } else {
      this.pollTimerRef = setInterval(async () => {
        if (this.comm === CommState.POLL) {
          await this.poll();
        }
      }, POLL_INTERVAL);
    }
  };

  // Send the current Editor Steps to the Authority Stream.
  private sendSteps = async () => {
    // FAIL state nothing to do
    if (this.comm === CommState.FAIL) {
      return;
    }

    const sendable = sendableSteps(this.editor.state);
    if (sendable) {
      this.comm = CommState.SEND;

      const transaction = {
        version: sendable.version,
        steps: sendable.steps,
        client_id: sendable.clientID.toString(),
      };

      const result = await this.httpClient.appendTransaction(transaction);
      if (result.status === "ok") {
        const sendableChange = sendableWordCountChange(sendable);
        const handled = this.receiveSteps(
          transaction.steps,
          transaction.steps.map(() => transaction.client_id),
          transaction.version,
          (state) =>
            this.onSnapshot?.({
              doc: state.doc.toJSON(),
              version: getVersion(state),
              wordCountChange: sendableChange,
            })
        );
        if (!handled) {
          this.sendSteps();
        } else {
          // Caret after steps have been accepted
          this.sendCaret();
        }

        this.comm = CommState.POLL;
      }
      if (result.status === "conflict") {
        this.comm = CommState.POLL;
        await this.poll();
      }
    }
  };

  // Publish the current editor Selection to the StudentChannel as an
  // `client.selection` message.
  private sendCaret = () => {
    // FAIL state nothing to do
    if (this.comm === CommState.FAIL) {
      return;
    }
    if (this.channel) {
      this.channel.push("client.selection", {
        client_id: this.clientId,
        selection: {
          kind: "editor",
          stream_id: this.streamId,
          anchor: this.editor.state.selection.anchor,
          head: this.editor.state.selection.head,
        },
      } satisfies EditorSelectionMsg);
    }
  };

  // Draw carets for connected clients with selections in the Stream connected
  // to this Editor.
  private drawCarets = () => {
    if (this.presence) {
      const carets: { clientId: string; head: number }[] = [];
      this.presence.list((_userId, { metas }: { metas: PresenceMeta[] }) => {
        for (const meta of metas) {
          if (
            meta.client &&
            meta.client.selection &&
            meta.client.selection.kind === "editor" &&
            meta.client.selection.stream_id === this.streamId
          ) {
            carets.push({
              clientId: meta.client.client_id,
              head: meta.client.selection.head,
            });
          }
        }
      });
      if (carets.length > 0) {
        this.editor.commands.updateCarets(carets);
      }
    }
  };

  private clean = () => {
    if (this.channelRefs.length > 0 && this.channel) {
      for (const [event, ref] of this.channelRefs) {
        this.channel.off(event, ref);
      }
    }
    if (this.pollTimerRef) {
      clearInterval(this.pollTimerRef);
    }
  };
}

// Types

// Client side editor snapshot
interface SnapshotInput {
  doc: JSONContent;
  version: number;
  wordCountChange: SendableWordCountChange;
}

// Communication state of the StreamConnection instance
enum CommState {
  INIT = "init",
  POLL = "poll",
  SEND = "send",
  FAIL = "fail",
}

// Payload for `stream.version` channel message indicating that a new Stream
// version has been confirmed.
interface StreamVersionMsg {
  // The updated stream
  stream_id: string;
  // The client that sent the update
  client_id: string;
  // The new version of the stream
  version: number;
}

// Payload for `client.selection` channel message indicating a Selection update
// in a Editor connected to the collaborative Stream.
interface EditorSelectionMsg {
  client_id: string;
  selection: {
    kind: "editor";
    stream_id: string;
    anchor: number;
    head: number;
  };
}

//////////////////////////////////////////////////////////////////////////////
// HTTP API                                                                 //
//////////////////////////////////////////////////////////////////////////////

interface Transaction {
  version: number;
  steps: readonly Step[];
  client_id: string;
}

interface AppendResult {
  status: "ok" | "conflict" | "error";
}

interface ReadStepsResult {
  steps: ServerStep[];
  version: number;
}

interface ServerStep {
  /** Step Data */
  data: unknown;
  /** Client ID that sent the step */
  client_id: string;
}

interface Stream {
  stream_id: string;
  version: number;
  initial_state: JSONContent;
}

interface APIProps {
  /// Cadmus Tenant
  tenant: string;
  /// Authority Stream ID
  streamId: string;
  /// Optional Student Channel topic used for broadcasting events to assist in
  /// collaboration.
  studentTopic?: string;
}

class AuthorityAPI {
  tenant: string;
  streamId: string;
  studentTopic?: string;

  constructor(props: APIProps) {
    this.tenant = props.tenant;
    this.streamId = props.streamId;
    this.studentTopic = props.studentTopic;
  }

  /**
   * Ensure a Stream is setup for an Answer Block.
   *
   * @param answerBlockId - The Answer Block ID to use as the Stream ID.
   * @param initialState - The initial state of the Stream at version 0.
   * @returns Promise that resolves to the Stream object.
   *
   * @throws Error if the Stream could not be created. In this case it's better to
   *     reload the application.
   */
  async setupAuthorityStream(initialState: JSONContent): Promise<Stream> {
    const headers = new Headers({
      "x-cadmus-role": "student",
      "x-cadmus-tenant": this.tenant,
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    const resp = await fetch(`${API_ENDPOINT}/api/authority/streams`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({
        stream_id: this.streamId,
        initial_state: initialState,
      }),
    });

    if (resp.ok) {
      const stream: Stream = await resp.json();
      return stream;
    }

    throw new Error("StreamCreationError");
  }

  async appendTransaction(transaction: Transaction): Promise<AppendResult> {
    const headers = new Headers({
      "x-cadmus-role": "student",
      "x-cadmus-tenant": this.tenant,
      "Content-Type": "application/json",
    });

    const body = {
      transaction,
      student_topic: this.studentTopic,
    };

    const resp = await fetch(
      `${API_ENDPOINT}/api/authority/transactions/${this.streamId}`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(body),
      }
    );

    if (resp.ok && resp.status === 204) {
      return { status: "ok" };
    }

    if (resp.status === 409) {
      return { status: "conflict" };
    }

    return { status: "error" };
  }

  async readSteps(version: number): Promise<ReadStepsResult> {
    const headers = new Headers({
      "x-cadmus-role": "student",
      "x-cadmus-tenant": this.tenant,
      Accept: "application/json",
    });

    const resp = await fetch(
      `${API_ENDPOINT}/api/authority/steps/${this.streamId}?version=${version}`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (resp.ok) {
      const result: ReadStepsResult = await resp.json();
      return result;
    }

    return { steps: [], version };
  }
}
